import { useEffect, useState } from 'react';
import { ModerationNav } from '../components/ModerationNav';
import { fetchOutcomes, outcomesCsvUrl, type OutcomesReport, type MeResponse } from '../lib/api';

/**
 * Cohort outcomes dashboard — the moderator-only artifact for renewal
 * conversations with the campus. Shows pairing totals, UCLA-3 trend by
 * checkpoint, per-user change (% with a 3+ point drop), and a per-week
 * cohort breakdown. The CSV download is PII-free (pseudonymous
 * membership_id only).
 *
 * Bar charts render inline as SVG — no chart library so the bundle stays
 * small. Sage bars for the success categories (active, graduated),
 * terracotta for the abandonment categories.
 */
export function Outcomes({ me }: { me: MeResponse }) {
  const communityId = me.memberships[0]?.communityId ?? '';
  const [report, setReport] = useState<OutcomesReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!communityId) return;
    fetchOutcomes(communityId)
      .then(setReport)
      .catch(() => setError('Could not load the outcomes report.'));
  }, [communityId]);

  if (error) {
    return (
      <main className="wide">
        <div className="card">
          <p className="error">{error}</p>
        </div>
      </main>
    );
  }
  if (!report) {
    return (
      <main className="wide">
        <div className="card">
          <p className="muted">Loading the cohort report…</p>
        </div>
      </main>
    );
  }

  const total =
    report.pairings.active +
    report.pairings.graduated +
    report.pairings.abandoned +
    report.pairings.dead +
    report.pairings.other;
  const pairingMax = Math.max(
    1,
    report.pairings.active,
    report.pairings.graduated,
    report.pairings.abandoned,
    report.pairings.dead,
  );
  const checkpointMax = Math.max(9, ...report.checkpoints.map((c) => c.meanScore));
  const cohortMax = Math.max(1, ...report.cohorts.map((c) => c.nJoined));

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <h1>Cohort outcomes — for renewal conversations</h1>
        <p className="muted">
          What to show the campus when the contract comes up. All metrics are scoped to this
          community; nothing leaks across tenants.
        </p>

        <section className="outcomes-section">
          <h2>Pairing outcomes</h2>
          <p className="muted">
            {total} {total === 1 ? 'pairing' : 'pairings'} on record.{' '}
            {report.pairings.graduated > 0 && (
              <>
                <strong>{report.pairings.graduated}</strong>{' '}
                {report.pairings.graduated === 1 ? 'has' : 'have'} graduated cleanly
                {' — '}
              </>
            )}
            graduation is the positive end-state the product is designed around.
          </p>
          <PairingBars report={report} max={pairingMax} />
        </section>

        <section className="outcomes-section">
          <h2>Loneliness trend</h2>
          <p className="muted">
            UCLA-3 mean score (range 3–9 — lower is less lonely) at each checkpoint.{' '}
            <strong>{report.change.pctDroppedThreePlus.toFixed(1)}%</strong> of users with both an
            intake and a later check-in dropped <strong>3+ points</strong> (a strong clinically
            meaningful change — the standard threshold is ~2).
          </p>
          <CheckpointBars stats={report.checkpoints} max={checkpointMax} />
        </section>

        <section className="outcomes-section">
          <h2>Cohort by week</h2>
          <p className="muted">
            Memberships joined per week. The launch and any boost peaks live here.
          </p>
          <CohortBars cohorts={report.cohorts} max={cohortMax} />
        </section>

        <section className="outcomes-section">
          <h2>Export</h2>
          <p className="muted">
            One row per member — pseudonymous (membership_id only, no names or emails). For pasting
            into a slide or a renewal email.
          </p>
          <p className="stack-row">
            <a className="btn" href={outcomesCsvUrl(communityId)} download>
              Download CSV
            </a>
          </p>
        </section>
      </div>
    </main>
  );
}

function PairingBars({ report, max }: { report: OutcomesReport; max: number }) {
  const buckets: { label: string; n: number; color: string }[] = [
    { label: 'Active', n: report.pairings.active, color: 'var(--green)' },
    { label: 'Graduated', n: report.pairings.graduated, color: 'var(--green-deep)' },
    { label: 'Abandoned', n: report.pairings.abandoned, color: 'var(--clay)' },
    { label: 'Never started', n: report.pairings.dead, color: 'var(--ink-3)' },
  ];
  return (
    <ul className="bars">
      {buckets.map((b) => (
        <li key={b.label}>
          <span className="bars-label">{b.label}</span>
          <span className="bars-track" aria-hidden>
            <span
              className="bars-fill"
              style={{ width: `${(b.n / max) * 100}%`, background: b.color }}
            />
          </span>
          <span className="bars-value">{b.n}</span>
        </li>
      ))}
    </ul>
  );
}

const CHECKPOINT_LABEL: Record<string, string> = {
  intake: 'Intake',
  week_2: 'Week 2',
  week_6: 'Week 6',
  term_end: 'Term end',
  graduation: 'Graduation',
  ongoing: 'Ongoing',
};

function CheckpointBars({ stats, max }: { stats: OutcomesReport['checkpoints']; max: number }) {
  if (stats.length === 0) {
    return <p className="muted">No check-ins recorded yet.</p>;
  }
  return (
    <ul className="bars">
      {stats.map((s) => (
        <li key={s.checkpoint}>
          <span className="bars-label">{CHECKPOINT_LABEL[s.checkpoint] ?? s.checkpoint}</span>
          <span className="bars-track" aria-hidden>
            <span
              className="bars-fill"
              style={{ width: `${(s.meanScore / max) * 100}%`, background: 'var(--green)' }}
            />
          </span>
          <span className="bars-value">
            {s.meanScore.toFixed(1)} <span className="bars-meta">(n={s.n})</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function CohortBars({ cohorts, max }: { cohorts: OutcomesReport['cohorts']; max: number }) {
  if (cohorts.length === 0) {
    return <p className="muted">No memberships yet.</p>;
  }
  return (
    <ul className="bars">
      {cohorts.map((c) => (
        <li key={c.cohortWeek}>
          <span className="bars-label">{c.cohortWeek}</span>
          <span className="bars-track" aria-hidden>
            <span
              className="bars-fill"
              style={{ width: `${(c.nJoined / max) * 100}%`, background: 'var(--green-soft)' }}
            />
          </span>
          <span className="bars-value">{c.nJoined}</span>
        </li>
      ))}
    </ul>
  );
}
